import z from 'zod';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';

import {isValidEmail} from 'shared/web';

import {REPLY_SUBJECT_MAX_LENGTH} from '../../../constants/REPLY_SUBJECT_MAX_LENGTH';
import {refineEmailBody} from '../../../utils/refineEmailBody';

const buildSchema = (options: {isReply: boolean}) =>
  z
    .object({
      subject: (options.isReply
        ? z.string().max(REPLY_SUBJECT_MAX_LENGTH, i18n.t('errors.field_is_too_long'))
        : z.string()
      ).optional(),
      body: z.string().optional(),
      customerId: z.string(),
      copyTo: z.array(z.string()).optional(),
      smtpCredentialsId: z.string(),
      customerMessageId: z
        .array(
          z.enum(['customer-message', 'service-customer_check_in', 'service-customer_handover'])
        )
        .min(1, {message: i18n.t('errors.field_is_required')}),
      // The rule of the send endpoint, so a recipient the form accepts is never refused on send
      customerEmailAddress: z.string().refine(isValidEmail, {
        message: i18n.t('errors.invalid_string.email'),
      }),
    })
    .refine(
      (data) => {
        if (data.customerMessageId?.[0] === 'customer-message') {
          return isNotNilOrEmpty(data.subject);
        }
        return true;
      },
      {
        message: i18n.t('errors.field_is_required'),
        path: ['subject'],
      }
    )
    // Only the custom message carries an author written body; the other ids render from a template,
    // so neither the emptiness nor the length rule applies to them.
    .superRefine((data, ctx) => {
      if (data.customerMessageId?.[0] !== 'customer-message') {
        return;
      }

      const result = refineEmailBody(z.string()).safeParse(data.body ?? '');
      if (result.success) {
        return;
      }

      result.error.issues.forEach((issue) =>
        ctx.addIssue({code: z.ZodIssueCode.custom, path: ['body'], message: issue.message})
      );
    });

export const SendCustomerEmailFormSchema = buildSchema({isReply: false});
export const SendCustomerEmailReplyFormSchema = buildSchema({isReply: true});

export type FormValues = z.infer<typeof SendCustomerEmailFormSchema>;
